import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  ISirena,
  IUpdateSirena,
  ICliente,
  IUpdateCliente,
  IQueryParam,
  IListado,
  ICoordenadas,
} from 'modelos/src';
import { HelperService } from '../../../auxiliares/servicios/helper.service';
import { SirenasService } from '../sirenas.service';
import { AuthService } from '../../login/auth.service';
import { ClientesService } from '../../clientes/clientes.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { ListadosService } from 'src/app/auxiliares/servicios/listados.service';

@Component({
  selector: 'app-editar-sirenas',
  templateUrl: './editar-sirenas.component.html',
  styleUrls: ['./editar-sirenas.component.scss'],
  standalone: false,
})
export class EditarSirenasComponent implements OnInit, OnDestroy {
  private fb = inject(UntypedFormBuilder);
  private service = inject(SirenasService);
  helper = inject(HelperService);
  private route = inject(ActivatedRoute);
  private auth = inject(AuthService);
  private clientesService = inject(ClientesService);
  private listadosService = inject(ListadosService);

  public coordenadas?: ICoordenadas;

  get datosHw(): UntypedFormGroup {
    return this.formulario?.get('datosHw') as UntypedFormGroup;
  }

  get configs(): UntypedFormGroup {
    return this.formulario?.get('configs') as UntypedFormGroup;
  }

  public editarUbicacion?: boolean;

  public cliente?: ICliente;
  public tiposDeSirena: string[] = [];

  public clientes: ICliente[] = [];
  public clientes$?: Subscription;

  formulario?: UntypedFormGroup;
  loading = false;
  title?: string;

  public id: string | null = null;
  public data?: ISirena;

  // Mapa

  private async initMap(): Promise<void> {
    const lat = this.data?.ubicacionManual?.lat;
    // Setear posicion incial
    if (!lat) {
      const currPos = await this.helper.getCurrentPosition();
      this.coordenadas = currPos;
    }
    // Definir mapa
    this.coordenadas = this.data?.ubicacionManual || this.coordenadas;
  }

  private crearFormulario(): void {
    this.formulario = this.fb.group({
      idCliente: [this.data?.idCliente],
      activa: [this.data?.activa],
      tipo: this.data?.tipo ? [[this.data?.tipo]] : [[]],
      datosHw: this.fb.group({
        numeroNac: [this.data?.datosHw?.numeroNac],
        version: [this.data?.datosHw?.version],
        fuenteExterna: [this.data?.datosHw?.fuenteExterna],
        fechaDeFabricacion: [this.data?.datosHw?.fechaDeFabricacion],
      }),

      configs: this.fb.group({
        distanciaCobertura: [this.data?.configs?.distanciaCobertura],
        puedeEnviarControlesDesconocidos: [
          this.data?.configs?.puedeEnviarControlesDesconocidos,
        ],
      }),
    });
  }

  public volver(): void {
    window.history.back();
  }

  //

  private getData(): IUpdateSirena {
    const tipo = this.formulario?.get('tipo')?.value;
    const datos: IUpdateSirena = {
      activa: this.formulario?.get('activa')?.value,
      idCliente: this.formulario?.get('idCliente')?.value,
      tipo: tipo[0],
      datosHw: {
        numeroNac: this.formulario?.get('datosHw')?.get('numeroNac')?.value,
        version: this.formulario?.get('datosHw')?.get('version')?.value,
        fuenteExterna: this.formulario?.get('datosHw')?.get('fuenteExterna')
          ?.value,
        fechaDeFabricacion: this.formulario
          ?.get('datosHw')
          ?.get('fechaDeFabricacion')?.value,
      },
      configs: {
        distanciaCobertura: this.formulario
          ?.get('configs')
          ?.get('distanciaCobertura')?.value,
        puedeEnviarControlesDesconocidos: this.formulario
          ?.get('configs')
          ?.get('puedeEnviarControlesDesconocidos')?.value,
      },
    };
    if (this.editarUbicacion) {
      datos.ubicacionManual = this.coordenadas;
    }
    return datos;
  }

  public async enviar(): Promise<void> {
    this.loading = true;
    try {
      const data = this.getData();
      // Las sirenas no se crean desde el admin: solo se editan las existentes.
      if (!this.data?._id) {
        this.helper.notifError('Sirena no encontrada');
        this.loading = false;
        return;
      }
      await firstValueFrom(this.service.editar(this.data._id, data));
      this.helper.notifSuccess('Editado correctamente');
      this.volver();
    } catch (err) {
      console.error(err);
      this.helper.notifError(err);
    }
    this.loading = false;
  }

  private async listar(id: string): Promise<void> {
    this.data = await firstValueFrom(this.service.listarPorId(id));
    console.log('data', this.data);
  }

  private async listarClientes(): Promise<void> {
    const query: IQueryParam = {
      sort: 'nombre',
      select: 'nombre',
    };

    this.clientes$?.unsubscribe();
    this.clientes$ = this.listadosService
      .subscribe<IListado<ICliente>>('clientes', query)
      .subscribe((data) => {
        this.clientes = data.datos;
        console.debug(new Date().toLocaleString(), `listado de clientes`, data);
      });
    await this.listadosService.getLastValue('clientes', query);
  }

  public async agregarTipos(tipo: string) {
    if (this.tiposDeSirena.includes(tipo)) return;

    this.loading = true;

    try {
      this.tiposDeSirena.push(tipo);

      const update: IUpdateCliente = {
        configuracion: {
          tiposDeSirena: this.tiposDeSirena,
        },
      };
      const cliente = await firstValueFrom(
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        this.clientesService.editar(this.cliente?._id!, update),
      );

      this.auth.setCliente(cliente);
      // console.log('update', update);
    } catch (error) {
      this.helper.notifError(error);
      console.error(error);
    } finally {
      this.loading = false;
    }
  }
  ///

  async ngOnInit(): Promise<void> {
    this.loading = true;
    this.cliente = this.auth.getCliente();
    this.tiposDeSirena = this.cliente?.configuracion?.tiposDeSirena || [];
    const params = await firstValueFrom(this.route.paramMap);
    this.id = params.get('id');
    this.title = this.id ? 'Editar' : 'Crear';
    if (this.id) {
      await this.listar(this.id);
    }
    if (this.data?.ubicacionManual) {
      this.editarUbicacion = true;
    }
    await this.listarClientes();
    this.crearFormulario();
    await this.initMap();
    this.loading = false;
  }

  ngOnDestroy(): void {
    this.clientes$?.unsubscribe();
  }
}
